/**
 * Named call-audio transmit presets. LiveKit-free so settings storage and the
 * sidebar room handle can read them without pulling `livekit-client`.
 */

export const CALL_AUDIO_QUALITY_IDS = [
  'low',
  'standard',
  'high',
  'ultra',
  'vinylSnob',
  'molecularEuphoria',
] as const;
export type CallAudioQualityId = (typeof CALL_AUDIO_QUALITY_IDS)[number];

const GATED_CALL_AUDIO_QUALITY_IDS: readonly CallAudioQualityId[] = ['vinylSnob', 'molecularEuphoria'];

/** Default microphone transmit quality (96 kbps mono). */
export const DEFAULT_MIC_AUDIO_QUALITY: CallAudioQualityId = 'high';
/** Default screenshare audio transmit quality (128 kbps stereo). */
export const DEFAULT_SCREENSHARE_AUDIO_QUALITY: CallAudioQualityId = 'ultra';

export interface CallAudioQualitySpec {
  maxBitrate: number;
  /** Stereo capture. Publishing declares stereo either way — see `callAudioPublishFields`. */
  stereo: boolean;
}

export const CALL_AUDIO_QUALITY: Record<CallAudioQualityId, CallAudioQualitySpec> = {
  low: { maxBitrate: 24_000, stereo: false },
  standard: { maxBitrate: 48_000, stereo: false },
  high: { maxBitrate: 96_000, stereo: false },
  ultra: { maxBitrate: 128_000, stereo: true },
  vinylSnob: { maxBitrate: 192_000, stereo: true },
  molecularEuphoria: { maxBitrate: 320_000, stereo: true },
};

export function isCallAudioQualityId(value: string): value is CallAudioQualityId {
  return (CALL_AUDIO_QUALITY_IDS as readonly string[]).includes(value);
}

export function parseCallAudioQualityId(
  value: string | null | undefined,
  fallback: CallAudioQualityId,
): CallAudioQualityId {
  if (value && isCallAudioQualityId(value)) return value;
  return fallback;
}

export function callAudioQualityIsGated(id: CallAudioQualityId): boolean {
  return (GATED_CALL_AUDIO_QUALITY_IDS as readonly string[]).includes(id);
}

/** Insider subscription or Founder entitlement unlocks Vinyl Snob / Molecular Euphoria. */
export function hasCallAudioQualityUnlock(
  subscriptions: readonly string[],
  entitlements: readonly string[],
): boolean {
  return subscriptions.includes('insider') || entitlements.includes('founder');
}

/**
 * Quality actually published. Gated ids clamp to Ultra when locked; storage is
 * left alone so a later re-subscribe restores the saved premium choice.
 */
export function effectiveCallAudioQuality(
  id: CallAudioQualityId,
  unlocked: boolean,
): CallAudioQualityId {
  return callAudioQualityIsGated(id) && !unlocked ? 'ultra' : id;
}

/** Module flag for non-React callers (sidebar `toggleScreenShare`). */
let callAudioQualityUnlocked = false;

export function setCallAudioQualityUnlocked(value: boolean): void {
  callAudioQualityUnlocked = value;
}

export function getCallAudioQualityUnlocked(): boolean {
  return callAudioQualityUnlocked;
}

export function resolvePublishedCallAudioQuality(id: CallAudioQualityId): CallAudioQualityId {
  return effectiveCallAudioQuality(id, getCallAudioQualityUnlocked());
}

/**
 * Publish fields LiveKit reads from `audioPreset.maxBitrate` / `forceStereo`.
 * Shape matches livekit-client without importing it.
 *
 * Every preset declares stereo, mono captures included. A payload type has to
 * carry one codec configuration across a BUNDLE group (RFC 8843), and LiveKit
 * only appends `stereo=1` to the answer m-lines whose publisher declared it, so
 * a room mixing mono and stereo publishers describes Opus two different ways.
 * Chromium 142 logs that collision and applies the description anyway; newer
 * builds return it as an error behind a field trial. Declaring stereo on a mono
 * capture costs nothing — the source still carries one channel — and the presets
 * keep differing where it counts, since `maxBitrate` rides in the sender
 * encodings rather than in the SDP.
 *
 * `dtx` is explicit to hold it where it already was: LiveKit turns DTX off for
 * anything stereo, so leaving it unset would drop silence suppression from the
 * mono presets as a side effect of the line above. RED needs no such care —
 * LiveKit disables it outright whenever E2EE is on, which for us is always.
 */
export function callAudioPublishFields(id: CallAudioQualityId): {
  audioPreset: { maxBitrate: number };
  forceStereo: boolean;
  dtx: boolean;
} {
  const spec = CALL_AUDIO_QUALITY[id];
  return {
    audioPreset: { maxBitrate: spec.maxBitrate },
    forceStereo: true,
    dtx: !spec.stereo,
  };
}

export function callAudioChannelCount(id: CallAudioQualityId): 1 | 2 {
  return CALL_AUDIO_QUALITY[id].stereo ? 2 : 1;
}
